import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { showToastNotification } from 'welltrax-ui-components';
import { arrayErrorsExcludedText, CustomExceptions } from '../utils/CustomExceptions';
import { HttpTypes } from '../types/HttpTypes';

const config: AxiosRequestConfig = {
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Source: 'portal'
  },
  maxContentLength: 8000,
  maxRedirects: 5,
  responseType: 'json',
  timeout: 600000 * 2,
  withCredentials: true,
  validateStatus: (status: number) => status >= 200 && status < 300
};

function getToken() {
  const token = localStorage.getItem('token');
  if (token) {
    return token;
  }
  return '';
}

const getAuth0Token = () => {
  const auth0Token = localStorage.getItem('auth0Token');
  if (!!auth0Token) {
    return auth0Token;
  }
  return '';
};

const hasAuth0 = () => {
  const hasAuth0 = localStorage.getItem('hasAuth0');
  if (!!hasAuth0 && hasAuth0.toLowerCase() === 'true') {
    return true;
  }
  return false;
};

function clearToken(): void {
  localStorage.removeItem('userdata');
  localStorage.removeItem('token');
  localStorage.removeItem('readyLogout');
  localStorage.removeItem('hasAuth0');
  localStorage.removeItem('auth0Token');
}

const isSessionTimeout = (error: any, sessionExp: string) => {
  const err = new CustomExceptions(error.response.data).getCustomMessage();
  if (err === sessionExp) {
    setTimeout(() => {
      clearToken();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }, 200);
  }

  return err;
};

export function createHttpAxios(url: string, cnf?: AxiosRequestConfig): AxiosInstance {
  const resultConfig = { ...config, ...cnf, ...{ withCredentials: true } };
  resultConfig.baseURL = `${resultConfig.baseURL}${url}`;
  const axiosInstance = axios.create(resultConfig);

  axiosInstance.interceptors.request.use(
    (conf: any) => {
      if (!getToken() && !conf.baseURL.includes('/user/login')) {
        throw new axios.Cancel(HttpTypes.HTTP_CANCELED);
      }
      // TODO don't touch this
      if (!conf.headers.Authentication) {
        if (conf.baseURL.includes('/user/login') && hasAuth0())
          conf.headers.Authentication = `Bearer ${getAuth0Token()}`;
        else conf.headers.Authentication = `${getToken()}`;
      }

      conf.headers.hasAuth0 = `${hasAuth0()}`;
      conf.headers.auth0Token = `${getAuth0Token()}`;

      return conf;
    },
    error => {
      return Promise.reject(error);
    }
  );
  const sessionExp = 'Your session has timed out or you are not authorized';
  axiosInstance.interceptors.response.use(
    (response: any) => {
      if (response?.data?.statusCode > 200) {
        if (process.env.REACT_APP_DS !== 'DEPLOY') {
          if (response.data.message && response.data.message.includes(sessionExp)) {
            if (!localStorage.getItem('readyLogout')) {
              showToastNotification({
                message: sessionExp,
                type: 'danger',
                icon: 'error',
                timeout: 2000
              });
              localStorage.setItem('readyLogout', '');
            }
            clearToken();
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
            return Promise.reject(sessionExp);
          }
        }

        // todo add i18n
        const err = new CustomExceptions(response.data).getCustomMessage();
        if (!arrayErrorsExcludedText.some(value => err.startsWith(value))) {
          showToastNotification({
            message: err,
            type: 'danger',
            icon: 'error'
          });
        }
        return Promise.reject(err);
      }
      if (response && response.status === 204 && response.data === null) {
        const r = response;
        r.data = [];
        return r;
      }
      return response;
    },
    (error: any) => {
      if (error.message === HttpTypes.HTTP_CANCELED) {
        return;
      }
      let err = 'An error occurred on the server';
      if (error.response && error.response.status) {
        const status = error.response.status;
        if (401 === status || 403 === status) {
          err = 'Session Expired';
          clearToken();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        if (error.response.data) {
          if (400 === status) {
            err = error.response.data.error_description || err;
          } else if (500 === status) {
            err = isSessionTimeout(error, sessionExp);
          } else if (status > 405 && 499 <= status) {
            err = error.response.data.message || err;
          } else if (status === 422) {
            //mapbox api errors or wrong inputs
            err = error.response?.data?.code ? `${error.response?.data?.code}: ${error.response?.data?.message}` : err;
          }
        }

        // else if (500 === status) {
        //   err = 'An error occurred on the server';
        // }
      }
      showToastNotification({
        message: err,
        type: 'danger',
        icon: 'error'
      });
      return Promise.reject(err);
    }
  );

  return axiosInstance;
}
